/* eslint-disable react/prop-types */
import React, { Fragment, FunctionComponent, useState, useEffect } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import TextInput from "../library/TextInput";
import Button from "../shared/button/Button";
import {
  About,
  AboutTitle,
  AboutText,
  ListWrapper,
  Title,
  TitleColumn,
  List,
  Supplier,
  SupplierColumn,
  SupplierColumnLink,
  NoPS,
  SearchInputWrapper,
  SearchRow,
  ClearButton,
} from "./styles/psl-styles";

interface RecruiterUser {
  fullName: string;
}
interface ISupplierProps {
  id?: number;
  name?: string;
  createdAt?: string;
  recruiterId?: number;
  activeEngagementCount?: number;
  recruiter?: {
    id?: number;
    name?: string;
    recruiterUsers?: RecruiterUser[];
  };
}

export interface IEmployerPslListProps {
  suppliersList: Array<ISupplierProps>;
  setRemoveFromPslData: (event, { preferredSupplier: ISupplierProps }) => void;
  isPortalUser: boolean;
}

export const EmployerPslList: FunctionComponent<IEmployerPslListProps> = ({
  suppliersList,
  setRemoveFromPslData,
  isPortalUser,
}) => {
  const [filteredSupplierList, setFilteredSupplierList] = useState<
    ISupplierProps[]
  >([...suppliersList]);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredResults = (query: string) =>
    suppliersList.filter((supplier) => {
      const recruiterUserNames = supplier.recruiter.recruiterUsers
        .map((user) => user.fullName)
        .join(" ");
      const matchAgainst = [supplier.recruiter.name, recruiterUserNames].join(
        " "
      );
      return new RegExp(query, "i").exec(matchAgainst);
    });

  const noSuppliersYet = suppliersList.length < 1;

  const noPsText = noSuppliersYet
    ? "There are no preferred suppliers in your list yet."
    : "Your search returned no results.";

  const showSearchFilter = !noSuppliersYet;

  useEffect(() => {
    setFilteredSupplierList(filteredResults(searchQuery));
  }, [searchQuery, suppliersList]);

  return (
    <Fragment>
      <About className="animated fadeInUp delay-1">
        <AboutTitle>About</AboutTitle>
        {isPortalUser ? (
          <AboutText>
            If you wish to <span>add agencies</span> to your PSL please talk to
            your account manager at Portal.
          </AboutText>
        ) : (
          <AboutText>
            This is your <span>Preferred Suppliers List</span> (PSL). Any
            recruiter listed in here will be able to engage with you on any job
            you list without needing your approval. You can add a recruiter to
            your PSL through the <span>Your Jobs</span> page by clicking on the{" "}
            <span>Agencies tab</span>. You can remove a recruiter from your PSL
            at any time via this page.
          </AboutText>
        )}
      </About>
      {showSearchFilter && (
        <SearchRow className="animated fadeInUp delay-1">
          <SearchInputWrapper>
            <TextInput
              placeholder="Search by agency or recruiter name..."
              value={searchQuery}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSearchQuery(event.target.value);
              }}
              name="Search preferred suppliers"
              type="text"
              maxWidth={450}
            />
            {searchQuery && (
              <ClearButton
                data-test-id="psl-search-clear-button"
                onClick={() => {
                  setSearchQuery("");
                }}
              >
                <FontAwesomeIcon icon={faTimesCircle} />
              </ClearButton>
            )}
          </SearchInputWrapper>
        </SearchRow>
      )}

      <ListWrapper className="animated fadeInUp delay-2">
        {suppliersList.length > 0 && (
          <Title>
            <TitleColumn flex={3}>Recruiter</TitleColumn>
            <TitleColumn flex={2}>Date Added</TitleColumn>
            <TitleColumn flex={1}>Act. Eng.</TitleColumn>
            <TitleColumn flex={1}>Action</TitleColumn>
          </Title>
        )}
        <List>
          {filteredSupplierList.length > 0 ? (
            filteredSupplierList.map((supplier) => (
              <Supplier key={supplier.id}>
                <SupplierColumnLink
                  data-test-id="psl-recruiter-link"
                  data-turbolinks="false"
                  href={`/companies/${supplier.recruiter.id}/profile?back_to=/employer/preferred_suppliers`}
                  flex={3}
                >
                  {supplier.recruiter.name}
                </SupplierColumnLink>
                <SupplierColumn flex={2} noMobile>
                  {moment(supplier.createdAt).format("MMM Do, YYYY")}
                </SupplierColumn>
                <SupplierColumn flex={1} noMobile>
                  {supplier.activeEngagementCount}
                </SupplierColumn>
                <SupplierColumn flex={1} mobileAlignRight>
                  <Button
                    secondary
                    id="remove-from-psl-button"
                    buttonName="remove"
                    onClickAction={(e) =>
                      setRemoveFromPslData(e, {
                        preferredSupplier: supplier,
                      })
                    }
                  />
                </SupplierColumn>
              </Supplier>
            ))
          ) : (
            <NoPS>{noPsText}</NoPS>
          )}
        </List>
      </ListWrapper>
    </Fragment>
  );
};

EmployerPslList.propTypes = {
  suppliersList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      createdAt: PropTypes.string,
      recruiterId: PropTypes.number,
      activeEngagementCount: PropTypes.number,
      recruiter: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        recruiterUsers: PropTypes.arrayOf(
          PropTypes.shape({
            fullName: PropTypes.string,
          })
        ).isRequired,
      }).isRequired,
    })
  ).isRequired,
  setRemoveFromPslData: PropTypes.func.isRequired,
  isPortalUser: PropTypes.bool.isRequired,
};

interface IReduxState {
  preferredSuppliersList: {
    suppliersList: Array<ISupplierProps>;
    isPortalUser: boolean;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  suppliersList: state.preferredSuppliersList.suppliersList,
  isPortalUser: state.preferredSuppliersList.isPortalUser,
});

export default connect(mapStateToProps, {})(EmployerPslList);
